import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { QrCode } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Pagination } from '@/components/members/pagination'
import { useServiceRequests } from '@/hooks/use-requests'
import { formatDate } from '@/lib/utils'
import { RequestDetailModal } from './request-detail-modal'
import { ReportQrModal } from './report-qr-modal'
import type { ServiceRequestStatus } from '@riverpark/types'

const PAGE_SIZE = 20

export const STATUS_BADGE: Record<
  ServiceRequestStatus,
  { variant: 'pending' | 'committee' | 'paid' | 'draft'; label: string }
> = {
  open: { variant: 'pending', label: 'Open' },
  in_progress: { variant: 'committee', label: 'In progress' },
  resolved: { variant: 'paid', label: 'Resolved' },
  closed: { variant: 'draft', label: 'Closed' },
}

export const TYPE_LABEL: Record<string, string> = {
  maintenance: 'Maintenance',
  billing_query: 'Billing query',
  complaint: 'Complaint',
  general: 'General',
}

const STATUS_FILTERS: { value: ServiceRequestStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
]

export function RequestsWorkspace() {
  const [searchParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<ServiceRequestStatus | 'all'>('all')
  const [selected, setSelected] = useState<string | null>(null)
  const [showQr, setShowQr] = useState(false)

  // Depends on the "open" param value (not just mount) so clicking a notification while already
  // on this page — a same-pathname navigation React Router won't remount for — still opens it.
  useEffect(() => {
    const openId = searchParams.get('open')
    if (openId) setSelected(openId)
  }, [searchParams])

  const { data, isLoading } = useServiceRequests({
    page,
    limit: PAGE_SIZE,
    ...(statusFilter !== 'all' && { status: statusFilter }),
  })
  const items = data?.data ?? []
  const meta = data?.meta ?? { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 }

  return (
    <PageShell>
      <PageHeader
        label="Support"
        title="Service requests"
        description="Triage and respond to member requests."
        action={
          <Button variant="secondary" size="sm" onClick={() => setShowQr(true)}>
            <QrCode size={14} className="mr-1.5" />
            Get QR code
          </Button>
        }
      />

      <div className="flex gap-1 border-b border-border-2">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => {
              setStatusFilter(f.value)
              setPage(1)
            }}
            className={
              statusFilter === f.value
                ? 'border-b-2 border-primary px-3 py-2 font-sans text-sm font-medium text-ink'
                : 'px-3 py-2 font-sans text-sm text-muted-foreground hover:text-ink'
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="font-sans text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
          No requests here.
        </p>
      ) : (
        <div className="overflow-x-auto rounded border border-border-2">
          <table className="w-full font-sans text-sm">
            <thead>
              <tr className="border-b border-border-2 bg-cream-2 text-left text-label uppercase tracking-[0.08em] text-muted-foreground">
                <th className="px-4 py-3">Subject</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Reporter</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Received</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-border-2 last:border-0">
                  <td className="px-4 py-3 font-medium text-ink">{r.subject}</td>
                  <td className="px-4 py-3 text-ink">{TYPE_LABEL[r.type]}</td>
                  <td className="px-4 py-3 text-ink">
                    {r.memberName ?? (
                      <span className="inline-flex items-center gap-1.5">
                        <Badge variant="pending">Public</Badge>
                        {r.reporterName && (
                          <span className="text-muted-foreground">{r.reporterName}</span>
                        )}
                      </span>
                    )}
                    {r.plotNumber && (
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        Plot {r.plotNumber}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={STATUS_BADGE[r.status].variant}>
                      {STATUS_BADGE[r.status].label}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {formatDate(new Date(r.createdAt * 1000))}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="secondary" size="sm" onClick={() => setSelected(r.id)}>
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {meta.total > 0 && (
        <Pagination
          page={meta.page}
          totalPages={meta.totalPages}
          total={meta.total}
          limit={meta.limit}
          onPageChange={setPage}
          emptyLabel="No requests"
          className="rounded border border-border-2 bg-card"
        />
      )}

      {selected && (
        <RequestDetailModal
          requestId={selected}
          canChangeStatus
          onClose={() => setSelected(null)}
        />
      )}
      {showQr && <ReportQrModal onClose={() => setShowQr(false)} />}
    </PageShell>
  )
}
